/**
 * Brand middleware tags (client-safe).
 *
 * Resolves the request host to a payment {@link Brand} and exposes it to RPC
 * handlers via {@link CurrentBrand}, so the Stripe consumers can select the
 * matching account. No server dependencies — just tag declarations.
 *
 * @since 1.0.0
 */
import { RpcMiddleware } from "@effect/rpc";
import { Context } from "effect";
import type { Brand } from "../config/brands.ts";

/**
 * Context provided by {@link BrandMiddleware}: the brand resolved from the
 * request host. Handlers read this to pick the right Stripe account via
 * `StripeClients.forBrand`.
 *
 * @since 1.0.0
 */
export class CurrentBrand extends Context.Tag("CurrentBrand")<
  CurrentBrand,
  { readonly brand: Brand }
>() {}

/**
 * Brand-resolution middleware tag. Resolves the request host to a {@link Brand}
 * and provides {@link CurrentBrand}. Never fails — unknown hosts fall back to
 * the default brand — so no `failure` schema is declared.
 *
 * @since 1.0.0
 */
export class BrandMiddleware extends RpcMiddleware.Tag<BrandMiddleware>()(
  "BrandMiddleware",
  {
    provides: CurrentBrand,
  },
) {}
